import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import {
  createFoodIngredient,
  fetchFoodCategories,
  type FoodIngredient,
} from "../foodApi";
import { COMMON_UNITS } from "../utils/foodFormatters";

type Props = {
  onClose: () => void;
  /** Called with the newly created ingredient so callers can react (e.g. auto-map a line item). */
  onCreated?: (ingredient: FoodIngredient) => void;
  /** Pre-fill the name field (useful when creating from an invoice line description). */
  defaultName?: string;
};

export function AddIngredientModal({ onClose, onCreated, defaultName = "" }: Props) {
  const queryClient = useQueryClient();
  const nameRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(defaultName);
  const [category, setCategory] = useState("");
  const [standardUnit, setStandardUnit] = useState("");
  const [costOverride, setCostOverride] = useState("");
  const [error, setError] = useState<string | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ["food", "categories"],
    queryFn: fetchFoodCategories,
  });

  const ingredientCategories = (categoriesQuery.data ?? [])
    .filter((c) => c.type === "ingredient" && c.isActive)
    .map((c) => c.name);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const createMutation = useMutation({
    mutationFn: () =>
      createFoodIngredient({
        name: name.trim(),
        category,
        standardUnit,
        costOverride: costOverride ? parseFloat(costOverride) : null,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["food", "ingredients"] });
      queryClient.invalidateQueries({ queryKey: ["food", "dashboard"] });
      onCreated?.(created);
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to create ingredient");
    },
  });

  function handleSubmit() {
    setError(null);
    if (!name.trim()) { setError("Name is required."); return; }
    if (!category) { setError("Please select a category."); return; }
    if (!standardUnit) { setError("Please select a standard unit."); return; }
    createMutation.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md rounded-xl border border-tribal-border bg-tribal-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
          <h2 className="text-lg font-semibold text-white">Add Ingredient</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-tribal-muted transition-colors hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              Name
            </label>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Chicken Breast"
              onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
              className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-gray-300 focus:border-tribal-red focus:outline-none"
            >
              <option value="" disabled>Select a category…</option>
              {ingredientCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              Standard Unit
            </label>
            <select
              value={standardUnit}
              onChange={(e) => setStandardUnit(e.target.value)}
              className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-gray-300 focus:border-tribal-red focus:outline-none"
            >
              <option value="" disabled>Select a unit…</option>
              {COMMON_UNITS.map((unit) => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              Cost per Unit{standardUnit ? ` (per ${standardUnit})` : ""}
              <span className="ml-1 font-normal text-tribal-muted/70">— optional</span>
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-tribal-muted">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={costOverride}
                onChange={(e) => setCostOverride(e.target.value)}
                placeholder="0.00"
                onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                className="w-full rounded-lg border border-tribal-border bg-tribal-bg pl-7 pr-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
              />
            </div>
          </div>

          {error && (
            <p className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-tribal-border px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-tribal-muted transition-colors hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="rounded-lg bg-tribal-red px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-50"
          >
            {createMutation.isPending ? "Creating…" : "Add Ingredient"}
          </button>
        </div>
      </div>
    </div>
  );
}
